"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense } from "react";
import { usePathname } from "next/navigation";
import { routes } from "@/lib/routes";
import { strings } from "@/lib/strings";
import { isSellerRefreshPage } from "@/lib/chat/seller-refresh-context";
import { isPachtArticlePath } from "@/lib/content/pacht-articles";
import { useTurnstile } from "@/lib/turnstile";
import { useWidgetVisibility } from "@/components/chat/hooks/useWidgetVisibility";
import { ChatPanel } from "@/components/chat/ChatPanel";
import { ContextualCard } from "@/components/chat/ContextualCard";
import { InvitationCard } from "@/components/chat/InvitationCard";
import { CompactCircle } from "@/components/chat/CompactCircle";

/**
 * Floating chat widget (bottom-right corner).
 *
 * Visual modes:
 * 1. **Contextual card** — on form pages. Shows a step-specific question
 *    (e.g. "Was ist die UNB?") that updates on each step navigation.
 *    Dismissable -> collapses to compact circle with notification dot.
 *    Reappears on step change.
 * 2. **Invitation card** — the default on non-form pages. Shows a persuasive
 *    card until the user engages (opens chat or dismisses).
 * 3. **Compact circle** — after the user has engaged (on non-form pages),
 *    or after dismissing contextual card (on form pages). Lease articles start
 *    here: they point their readers, landowners, to LandVest instead.
 *
 * On form pages, the widget sits above the sticky MicroStep navigation
 * bar on mobile to avoid blocking the Back/Next CTAs.
 */
export function ChatWidget() {
  const $ = _c(7);
  const pathname = usePathname();
  const isAdminRoute = /^\/admin(\/|$)/.test(pathname);
  if (isAdminRoute) {
    return null;
  }
  if (pathname === routes.flaechenPruefen) {
    return null;
  }
  let t0;
  if ($[0] !== pathname) {
    t0 = isSellerRefreshPage(pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== pathname) {
    t1 = isPachtArticlePath(pathname);
    $[2] = pathname;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = <Suspense fallback={null}><ChatWidgetInner sellerRefresh={t0} suppressInvitation={t1} /></Suspense>;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function ChatWidgetInner(t0) {
  const $ = _c(41);
  const {
    sellerRefresh,
    suppressInvitation
  } = t0;
  const {
    requestInteractiveToken
  } = useTurnstile();
  let t1;
  if ($[0] !== suppressInvitation) {
    t1 = {
      suppressInvitation
    };
    $[0] = suppressInvitation;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    state,
    visible,
    panelMounted,
    anonAuthReady,
    showContextualCard,
    showFormCircle,
    showOriginalInvitation,
    showCompactCircle,
    stepPrompt,
    stepKey,
    formBottom,
    defaultBottom,
    posthogDistinctId,
    handleOpen,
    handleClose,
    handleMinimize,
    handleDismissInvitation,
    handleDismissContextual
  } = useWidgetVisibility(strings, t1);
  if (!visible && !(sellerRefresh && state === "open")) {
    return null;
  }
  const circleLabel = strings.chat?.widgetLabel ?? "Chat \xF6ffnen";
  const dismissLabel = strings.chat?.close ?? "Schlie\xDFen";
  let t2;
  if ($[2] !== anonAuthReady || $[3] !== handleClose || $[4] !== handleMinimize || $[5] !== panelMounted || $[6] !== posthogDistinctId || $[7] !== requestInteractiveToken || $[8] !== state) {
    t2 = panelMounted && <div className={`fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-[60] ${state !== "open" ? "hidden" : ""}`}><ChatPanel authReady={anonAuthReady} onClose={handleClose} onMinimize={handleMinimize} requestInteractiveToken={requestInteractiveToken} posthogDistinctId={posthogDistinctId} /></div>;
    $[2] = anonAuthReady;
    $[3] = handleClose;
    $[4] = handleMinimize;
    $[5] = panelMounted;
    $[6] = posthogDistinctId;
    $[7] = requestInteractiveToken;
    $[8] = state;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== formBottom || $[11] !== handleDismissContextual || $[12] !== handleOpen || $[13] !== sellerRefresh || $[14] !== showContextualCard || $[15] !== stepKey || $[16] !== stepPrompt) {
    t3 = !sellerRefresh && showContextualCard && stepPrompt && <ContextualCard stepKey={stepKey} stepPrompt={stepPrompt} position={formBottom} dismissLabel={dismissLabel} ctaLabel={strings.chat?.contextualCta ?? "Klara kann helfen"} onOpen={handleOpen} onDismiss={handleDismissContextual} />;
    $[10] = formBottom;
    $[11] = handleDismissContextual;
    $[12] = handleOpen;
    $[13] = sellerRefresh;
    $[14] = showContextualCard;
    $[15] = stepKey;
    $[16] = stepPrompt;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== formBottom || $[19] !== handleOpen || $[20] !== sellerRefresh || $[21] !== showFormCircle) {
    t4 = !sellerRefresh && showFormCircle && <CompactCircle onClick={handleOpen} label={circleLabel} position={formBottom} showDot={true} />;
    $[18] = formBottom;
    $[19] = handleOpen;
    $[20] = sellerRefresh;
    $[21] = showFormCircle;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== defaultBottom || $[24] !== handleDismissInvitation || $[25] !== handleOpen || $[26] !== sellerRefresh || $[27] !== showOriginalInvitation) {
    t5 = !sellerRefresh && showOriginalInvitation && <InvitationCard position={defaultBottom} title={strings.chat?.invitationTitle ?? "Fragen zur Kompensation?"} body={strings.chat?.invitationBody ?? "Ich helfe Ihnen kostenfrei und unverbindlich weiter."} ctaLabel={strings.chat?.invitationCta ?? "Jetzt chatten"} dismissLabel={dismissLabel} onOpen={handleOpen} onDismiss={handleDismissInvitation} />;
    $[23] = defaultBottom;
    $[24] = handleDismissInvitation;
    $[25] = handleOpen;
    $[26] = sellerRefresh;
    $[27] = showOriginalInvitation;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  let t6;
  if ($[29] !== defaultBottom || $[30] !== handleOpen || $[31] !== sellerRefresh || $[32] !== showCompactCircle || $[33] !== state) {
    t6 = !sellerRefresh && showCompactCircle && <CompactCircle onClick={handleOpen} label={circleLabel} position={defaultBottom} showDot={state === "minimized"} />;
    $[29] = defaultBottom;
    $[30] = handleOpen;
    $[31] = sellerRefresh;
    $[32] = showCompactCircle;
    $[33] = state;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] !== t2 || $[36] !== t3 || $[37] !== t4 || $[38] !== t5 || $[39] !== t6) {
    t7 = <div data-above-modal={true} className="contents">{t2}{t3}{t4}{t5}{t6}</div>;
    $[35] = t2;
    $[36] = t3;
    $[37] = t4;
    $[38] = t5;
    $[39] = t6;
    $[40] = t7;
  } else {
    t7 = $[40];
  }
  return t7;
}